import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { IntentValuesType } from 'Deer/intent/types/IntentCommonTypes';

import { past3Months } from 'constants/ProjectConstants';

export type DisciplinaryActionsIntentType = {
  id: string;
  label: string;
  onClick: () => void;
  visible: () => boolean;
  value: {
    myData?: IntentValuesType;
  };
  category: 'Manage' | 'Analyze' | 'Configure';
  isDefault?: boolean;
  hasMetaFilters?: boolean;
};

export type CompaniesMapType = {
  id: number;
  companyName: string;
}[];

const initialState: {
  selectedIntent?: DisciplinaryActionsIntentType;
  allCompanies: CompaniesMapType;
  selectedCompanies: CompaniesMapType;
  allLocations: string[];
  selectedLocations: string[];
  groupByOptions: {
    uuid: string;
    keyName: string;
    displayName: string;
    enabled: boolean;
  }[];
  splitByOptions: {
    uuid: string;
    keyName: string;
    displayName: string;
    enabled: boolean;
  }[];
  selectedGroupBy: {
    uuid: string;
    value: string;
    label: string;
  } | null;
  selectedSplitBy: {
    uuid: string;
    value: string;
    label: string;
  } | null;
  reportDateRange: {
    startDate: Date;
    endDate: Date;
  };
} = {
  selectedIntent: null,
  allCompanies: [],
  selectedCompanies: [],
  allLocations: [],
  selectedLocations: [],
  groupByOptions: [
    {
      uuid: 'EMPLOYEE',
      keyName: 'EMPLOYEE',
      displayName: 'Employee',
      enabled: true,
    },
  ],
  splitByOptions: [
    {
      uuid: 'VIOLATION_TYPE',
      keyName: 'VIOLATION_TYPE',
      displayName: 'Violation Type',
      enabled: true,
    },
    {
      uuid: 'SEVERITY',
      keyName: 'SEVERITY',
      displayName: 'Severity',
      enabled: true,
    },
  ],
  selectedGroupBy: {
    uuid: 'EMPLOYEE',
    value: 'EMPLOYEE',
    label: 'Employee',
  },
  selectedSplitBy: {
    uuid: 'VIOLATION_TYPE',
    value: 'VIOLATION_TYPE',
    label: 'Violation Type',
  },
  reportDateRange: {
    startDate: past3Months.startDate,
    endDate: past3Months.endDate,
  },
};

export const DisciplinaryActionIntentSlice = createSlice({
  name: 'disciplinaryActionsIntent',
  initialState,
  reducers: {
    setSelectedDisciplinaryActionIntent: (state, action: PayloadAction<DisciplinaryActionsIntentType>) => {
      const newState = { ...state };
      newState.selectedIntent = action.payload;
      return newState;
    },
    setDisciplinaryActionAllCompanies: (state, action: PayloadAction<CompaniesMapType>) => {
      state.allCompanies = action.payload;
      return state;
    },
    setDisciplinaryActionSelectedCompanies: (state, action: PayloadAction<CompaniesMapType>) => {
      state.selectedCompanies = action.payload;
      return state;
    },
    setDisciplinaryActionAllLocations: (state, action: PayloadAction<string[]>) => {
      state.allLocations = action.payload;
      return state;
    },
    setDisciplinaryActionSelectedLocations: (state, action: PayloadAction<string[]>) => {
      state.selectedLocations = action.payload;
      return state;
    },
    setSelectedAdminReportGroupBy: (state, action: PayloadAction<{ uuid: string; value: string; label: string }>) => {
      state.selectedGroupBy = action.payload;
      return state;
    },
    setSelectedAdminReportSplitBy: (state, action: PayloadAction<{ uuid: string; value: string; label: string }>) => {
      state.selectedSplitBy = action.payload;
      return state;
    },
    setSelectedAdminReportDateRange: (state, action: PayloadAction<{ startDate: Date; endDate: Date }>) => {
      state.reportDateRange = action.payload;
      return state;
    },
  },
});

export const {
  setSelectedDisciplinaryActionIntent,
  setDisciplinaryActionAllCompanies,
  setDisciplinaryActionSelectedCompanies,
  setDisciplinaryActionAllLocations,
  setDisciplinaryActionSelectedLocations,
  setSelectedAdminReportGroupBy,
  setSelectedAdminReportSplitBy,
  setSelectedAdminReportDateRange,
} = DisciplinaryActionIntentSlice.actions;

export default DisciplinaryActionIntentSlice.reducer;

export const useSelectDisciplinaryActionsSelectedIntent = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.selectedIntent
  );

// Selector for all companies
export const useSelectDisciplinaryActionsAllCompanies = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.allCompanies
  );

// Selector for selected companies
export const useSelectDisciplinaryActionsSelectedCompanies = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.selectedCompanies
  );

// Selector for all locations
export const useSelectDisciplinaryActionsAllLocations = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.allLocations
  );

// Selector for selected locations
export const useSelectDisciplinaryActionsSelectedLocations = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.selectedLocations
  );

// Selector for group by options
export const useSelectDisciplinaryActionsGroupByOptions = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.groupByOptions
  );

// Selector for split by options
export const useSelectDisciplinaryActionsSplitByOptions = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.splitByOptions
  );

// Selector for selected group by
export const useSelectDisciplinaryActionsSelectedGroupBy = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.selectedGroupBy
  );

// Selector for selected split by
export const useSelectDisciplinaryActionsSelectedSplitBy = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.selectedSplitBy
  );

// Selector for report date range
export const useSelectDisciplinaryActionsReportDateRange = () =>
  useSelector(
    (state: { disciplinaryActionsIntent: typeof initialState }) => state.disciplinaryActionsIntent.reportDateRange
  );
